"use client";

import { useEffect, useId, useRef, type FormEvent } from "react";
import { X } from "lucide-react";
import { brandGradientCss } from "@tami/tokens";

import { Button } from "../button/button.web";
import { Input } from "../input/input.web";
import { ReusableModal } from "../modal/reusable-modal.web";
import { Textarea } from "../textarea/textarea.web";

export type PlanInquiryDialogStatus = "idle" | "sending" | "sent" | "error";

/**
 * Todo texto do diálogo — nada nasce escrito aqui dentro. Quem monta decide
 * `title`/`description` conforme o `status` (ex.: "Deu certo!" quando
 * `sent`); o pacote só exibe o que recebe.
 */
export interface PlanInquiryDialogLabels {
  readonly title: string;
  readonly description: string;
  readonly closeLabel: string;
  readonly nameLabel: string;
  readonly emailLabel: string;
  readonly whatsappLabel: string;
  readonly creatorsCountLabel: string;
  readonly messageLabel: string;
  readonly sentMessage: string;
  readonly errorMessage: string;
  readonly submitLabel: string;
  readonly submittingLabel: string;
}

export interface PlanInquiryDialogProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly status: PlanInquiryDialogStatus;
  readonly labels: PlanInquiryDialogLabels;
  readonly onSubmit: (data: FormData) => void;
}

/**
 * Contorno escurecido, altura mínima de toque e superfície clara dos campos,
 * na espessura pedida pela direção B.
 *
 * Paleta fixa da marca (`tami-dark`/`tami-paper`), não os tokens de tema
 * (`bg-primary`, `bg-card`...): esses trocam de valor com o esquema
 * claro/escuro do sistema, e a direção B pede um cabeçalho escuro e um corpo
 * claro fixos, iguais em qualquer esquema.
 */
const FIELD_CLASS = "h-11 border-2 border-tami-dark/55 bg-tami-paper text-tami-dark";

/**
 * Apresentação do formulário de contato do plano sob consulta (direção B de
 * `docs/design/bio-links/formulario-collab-direcao-b.html`): cabeçalho
 * quase-preto de ponta a ponta com a tarja de gradiente na base, corpo claro.
 *
 * A casca continua o `ReusableModal`; o cabeçalho e o "×" nascem desligados
 * (`showTitle`/`showCloseButton` a `false`) e este componente desenha os seus
 * por dentro do conteúdo. O `ReusableModal` já resolve o nome acessível do
 * diálogo sozinho nesse caso: mesmo com `showTitle={false}` ele guarda um
 * `<h2>` oculto ligado ao `aria-labelledby` do painel, preenchido pelo
 * `labels.title`.
 *
 * Sem estado, sem chamada de rede e sem texto próprio: `status` e `labels`
 * vêm de quem monta.
 */
export function PlanInquiryDialog({
  isOpen,
  onClose,
  status,
  labels,
  onSubmit,
}: PlanInquiryDialogProps) {
  // O <form> some da árvore quando o envio dá certo, levando o botão focado
  // junto — sem isto o foco cai no <body> e quem usa teclado ou leitor de
  // tela perde o lugar. O parágrafo de confirmação vira o próximo destino.
  const sentStatusRef = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    if (status === "sent") {
      sentStatusRef.current?.focus();
    }
  }, [status]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSubmit(new FormData(event.currentTarget));
  };

  // Prefixo por instância: duas instâncias deste diálogo na mesma tela (ex.:
  // dois gatilhos do mesmo plano) não podem colidir nos `id`/`htmlFor` dos
  // campos.
  const fieldIdPrefix = useId();
  const nameId = `${fieldIdPrefix}-name`;
  const emailId = `${fieldIdPrefix}-email`;
  const whatsappId = `${fieldIdPrefix}-whatsapp`;
  const creatorsCountId = `${fieldIdPrefix}-creators-count`;
  const messageId = `${fieldIdPrefix}-message`;

  return (
    // `overlayFullViewport`: por padrão o overlay desconta a largura da barra
    // lateral do produto, o que é certo dentro do app logado e errado aqui —
    // este formulário abre em páginas de marketing, que não têm barra.
    //
    // `showTitle`/`showCloseButton` a `false` desligam o cabeçalho e o "×"
    // padrão da casca. `showFooter` a `false` evita a faixa vazia que a casca
    // reserva para ações de rodapé, já que o botão de envio mora dentro do
    // formulário. `contentClassName` zera o preenchimento lateral/inferior
    // padrão do conteúdo para o cabeçalho poder ir de ponta a ponta; o corpo
    // do formulário reabre o respiro por conta própria, mais abaixo.
    <ReusableModal
      isOpen={isOpen}
      onClose={onClose}
      maxWidth="max-w-lg"
      overlayFullViewport
      fitContent
      mobileSheetFitContent
      showTitle={false}
      showCloseButton={false}
      showFooter={false}
      contentClassName="px-0 pb-0"
      title={labels.title}
      description={labels.description}
    >
      <div className="bg-tami-dark px-6 pb-4 pt-5">
        <div className="flex items-start justify-between gap-4">
          <h2 className="text-xl font-extrabold tracking-tight text-tami-light">
            {labels.title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={labels.closeLabel}
            className="-mr-2 -mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-tami-light transition-colors hover:bg-tami-light/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-tami-light"
          >
            <X className="h-5 w-5" />
            <span className="sr-only">{labels.closeLabel}</span>
          </button>
        </div>
        <p className="mt-2 text-sm leading-relaxed text-tami-light/80">
          {labels.description}
        </p>
      </div>
      {/* Tarja fina de gradiente na base do cabeçalho — único momento de cor
          da direção B. Cor da marca fixa, por isso `brandGradientCss` e não
          um token de tema. */}
      <div aria-hidden className="h-1" style={{ background: brandGradientCss(90) }} />

      <div className="bg-tami-paper px-6 py-5 text-tami-dark">
        {status === "sent" ? (
          <p
            ref={sentStatusRef}
            role="status"
            tabIndex={-1}
            className="py-6 text-center text-sm"
          >
            {labels.sentMessage}
          </p>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <label htmlFor={nameId} className="text-sm font-semibold">
                {labels.nameLabel}
              </label>
              <Input
                id={nameId}
                name="name"
                required
                maxLength={160}
                className={FIELD_CLASS}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor={emailId} className="text-sm font-semibold">
                {labels.emailLabel}
              </label>
              <Input
                id={emailId}
                name="email"
                type="email"
                required
                maxLength={320}
                className={FIELD_CLASS}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor={whatsappId} className="text-sm font-semibold">
                {labels.whatsappLabel}
              </label>
              <Input
                id={whatsappId}
                name="whatsapp"
                required
                maxLength={32}
                className={FIELD_CLASS}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor={creatorsCountId} className="text-sm font-semibold">
                {labels.creatorsCountLabel}
              </label>
              <Input
                id={creatorsCountId}
                name="creatorsCount"
                type="number"
                min={1}
                max={10000}
                className={FIELD_CLASS}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor={messageId} className="text-sm font-semibold">
                {labels.messageLabel}
              </label>
              <Textarea
                id={messageId}
                name="message"
                maxLength={2000}
                className="border-2 border-tami-dark/55 bg-tami-paper text-tami-dark"
              />
            </div>
            {/* Honeypot: escondido de gente, visível para bot que varre inputs. */}
            <input
              name="website"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden
              className="hidden"
            />
            {status === "error" ? (
              <p role="alert" className="text-sm font-semibold text-tami-pink">
                {labels.errorMessage}
              </p>
            ) : null}
            {/*
             * Paleta fixa (`bg-tami-dark`), pelo mesmo motivo do
             * `FIELD_CLASS`: o `Button` padrão pinta com `bg-primary`, que
             * inverte para claro no tema escuro. O envio em andamento usa o
             * `loading` do `Button` (TAM-282), igual aos demais botões.
             */}
            <Button
              type="submit"
              loading={status === "sending"}
              className="h-12 w-full bg-tami-dark text-base font-bold text-tami-light hover:bg-tami-dark-hover"
            >
              {labels.submitLabel}
            </Button>
          </form>
        )}
      </div>
    </ReusableModal>
  );
}
